import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@material-ui/core';
import { useLocation } from '@gatsbyjs/reach-router';
import { Link } from 'gatsby';
import React, { useState } from 'react';
import { CALENDAR_LINK } from '../../constants/demo-link';
import ENVS from '../../env.json';
import { useGdpr } from '../../hooks/useGdpr';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { sendCtaClickEvent } from '../../tracking';
import { PreFooterCta } from '../PreFooterCta';

const INK = COLORS.INK;
const FOOTER_TEXT = '#94a3b8';
const FOOTER_TEXT_BRIGHT = '#e2e8f0';

const Content = styled(Typography)``;

const CompanyName = styled('span')`
  font-size: 12px;
  color: ${FOOTER_TEXT};
`;

const FooterWrapper = styled('footer')`
  position: relative;
  font-size: 18px;
  line-height: 32px;
  padding: ${(p) => p.theme.spacing(8)}px ${(p) => p.theme.spacing(8)}px
    ${(p) => p.theme.spacing(6)}px;
  background: radial-gradient(
      ellipse 70% 60% at 50% 0%,
      rgba(24, 144, 255, 0.08) 0%,
      transparent 60%
    ),
    ${INK};
  color: ${FOOTER_TEXT};

  @media (max-width: 800px) {
    margin: 0;
    padding: ${(p) => p.theme.spacing(4)}px ${(p) => p.theme.spacing(2)}px
      ${(p) => p.theme.spacing(3)}px;
  }

  svg {
    width: 100%;
  }
`;

const Inner = styled('div')`
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: ${(p) => p.theme.spacing(3)}px;
  margin: 0 auto;
  padding: ${(p) => p.theme.spacing(2)}px;
`;

const List = styled('ul')`
  list-style-type: none;
  font-size: 15px;
  line-height: 32px;
  padding: 0;
  margin-top: 12px;

  li {
    margin-bottom: 4px;
  }

  a {
    color: ${FOOTER_TEXT};
    transition: color 0.2s;

    &:hover {
      color: #fff;
    }
  }
`;

const Wrapper = styled('div')`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  max-width: 1000px;
  width: 100%;
  margin: 0 auto;
  grid-column-gap: 24px;

  @media (max-width: 900px) {
    margin-top: 1rem;
  }

  @media (max-width: 750px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 550px) {
    grid-template-columns: 1fr;
  }
`;

const ListHeading = styled('div')`
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${FOOTER_TEXT_BRIGHT};

  @media (max-width: 500px) {
    margin-top: 1rem;
  }
`;

const Title = styled(Link)`
  display: flex;
  align-items: center;

  img {
    max-width: 200px;
    margin-right: 12px;
  }
`;

const LinkList = styled('ul')`
  color: ${FOOTER_TEXT};
  list-style-type: none;
  margin: 0;
  padding: 0;
  font-size: 13px;

  li {
    display: inline-block;
    padding: 0;
    margin-right: ${(p) => p.theme.spacing(3)}px;

    a {
      display: inline-block;
      color: ${FOOTER_TEXT};
      transition: color 0.2s;

      &:hover {
        color: #fff;
      }
    }
  }

  @media (max-width: 600px) {
    margin-top: ${(p) => p.theme.spacing(2)}px;
  }
`;

const EndFooter = styled('div')`
  display: grid;
  max-width: 1000px;
  margin: 50px auto 0;
  border-top: 1px solid rgba(148, 163, 184, 0.2);
  padding: ${(p) => p.theme.spacing(4)}px ${(p) => p.theme.spacing(2)}px;
  grid-template-columns: 1fr 1fr;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const Footer = () => {
  const { pathname } = useLocation();
  const { isEu, setAcceptedCookies } = useGdpr();
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);
  const showPrivacySettingsButton = isEu;

  return (
    <div>
      {pathname === '/' && <PreFooterCta />}
      <Dialog
        open={showPrivacyDialog}
        onClose={() => {
          setShowPrivacyDialog(false);
        }}
      >
        <DialogTitle>Adjust your privacy settings</DialogTitle>
        <DialogContent>
          <Content>
            This website uses cookies for analytics and advertising. Learn more
            in our <Link to="/privacy-policy/">privacy policy</Link>.
          </Content>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAcceptedCookies(false);
              setShowPrivacyDialog(false);
            }}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setAcceptedCookies(true);
              setShowPrivacyDialog(false);
            }}
          >
            Accept
          </Button>
        </DialogActions>
      </Dialog>
      <FooterWrapper>
        <Inner>
          <Wrapper>
            <div>
              <ListHeading>Platform</ListHeading>
              <List>
                <li>
                  <Link to="/product/">Product overview</Link>
                </li>
                <li>
                  <Link to="/product/unify/">Unify</Link>
                </li>
                <li>
                  <Link to="/product/optimize/">Optimize</Link>
                </li>
                <li>
                  <Link to="/product/amplify/">Amplify</Link>
                </li>
              </List>
            </div>
            <div>
              <ListHeading>AI Platform</ListHeading>
              <List>
                <li>
                  <Link to="/ai-platform/">AI platform overview</Link>
                </li>
                <li>
                  <Link to="/mcp/">Affilimate MCP</Link>
                </li>
              </List>
            </div>
            <div>
              <ListHeading>Solutions</ListHeading>
              <List>
                <li>
                  <Link to="/use-cases/">Publisher solutions</Link>
                </li>
                <li>
                  <Link to="/demand/">Brands &amp; agencies</Link>
                </li>
                <li>
                  <Link to="/integrations/">Integrations</Link>
                </li>
                <li>
                  <Link to="/pricing/">Pricing</Link>
                </li>
              </List>
            </div>
            <div>
              <ListHeading>Resources</ListHeading>
              <List>
                <li>
                  <Link to="/qs/faqs/">Pre-signup FAQs</Link>
                </li>
                <li>
                  <a href={ENVS.app.baseUrl} target="_blank">
                    Publisher log in
                  </a>
                </li>
                <li>
                  <a href="https://demand.affilimate.com/login" target="_blank">
                    Brand &amp; agency log in
                  </a>
                </li>
              </List>
            </div>
            <div>
              <ListHeading>Company</ListHeading>
              <List>
                <li>
                  <Link to="/about/">About</Link>
                </li>
                <li>
                  <Link to="/partners/">Partners</Link>
                </li>
                <li>
                  <a
                    href="https://www.linkedin.com/company/affilimate/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    LinkedIn
                  </a>
                </li>
                <li>
                  <Link to="/contact/">Contact</Link>
                </li>
                <li>
                  <a
                    href={CALENDAR_LINK}
                    target="_blank"
                    style={{ color: COLORS.PRIMARY_LIGHT }}
                    onClick={() => {
                      sendCtaClickEvent({ label: 'footer' });
                    }}
                  >
                    Get a demo
                  </a>
                </li>
              </List>
            </div>
          </Wrapper>
        </Inner>
        <EndFooter>
          <Title to="/">
            <img
              style={{ maxWidth: '100px' }}
              src="https://storage.googleapis.com/affilimate-assets/logos/logo-white-full.png"
              alt="Affilimate"
              width="100"
              height="22"
              loading="lazy"
            />
            <CompanyName>By Adaero Software</CompanyName>
          </Title>
          <LinkList>
            <li>Copyright &copy; 2026</li>
            <li>
              <Link to="/impressum/">Imprint</Link>
            </li>
            <li>
              <Link to="/qs/privacy-policy/">Privacy Policy</Link>
            </li>
            <li>
              {showPrivacySettingsButton && (
                <Button
                  style={{
                    color: FOOTER_TEXT,
                    fontSize: '13px',
                    padding: '0 0 3px 0'
                  }}
                  onClick={() => setShowPrivacyDialog(true)}
                >
                  Privacy settings
                </Button>
              )}
            </li>
          </LinkList>
        </EndFooter>
      </FooterWrapper>
    </div>
  );
};

export default Footer;
